import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { getNumberFormatter } from '@/utils/getNumberFormatter';

import { TickLabel } from '../charts/tick-label';
import { TrendArrow } from '../trend-arrow';
import type { ChartData } from '../types/chart';
import { TooltipContent } from './tooltip-content';

export const PopulationSizeChart = ({
  chartData,
  height,
  fontSize,
}: {
  chartData: ChartData;
  height: number;
  fontSize?: number;
}) => {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 90, bottom: 0 }}>
        <defs>
          <linearGradient id="colorValue3" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#5007BE" stopOpacity={0.8} />
            <stop offset="95%" stopColor="#5007BE" stopOpacity={0.1} />
          </linearGradient>
        </defs>

        <XAxis
          dataKey="title"
          axisLine={false}
          tickLine={false}
          fontSize={fontSize}
          interval={0}
          tick={({ x, y, payload }) => (
            <TickLabel
              x={x}
              y={y}
              dy={16}
              className="text-neutral-500"
              fill="currentcolor"
              fontSize={fontSize}
              value={payload.value}
            />
          )}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          fontSize={fontSize}
          domain={[
            (dataMin: number) => Math.floor(dataMin / 200) * 200,
            (dataMax: number) => Math.ceil(dataMax / 200) * 200,
          ]}
          tickFormatter={(value) => `$${value.toLocaleString()}`}
          tick={({ x, y, payload }) => (
            <TickLabel
              x={x}
              y={y}
              dy={4}
              dx={fontSize && fontSize > 20 ? -100 : -80}
              className="font-normal text-[#061B25]"
              textAnchor="start"
              fill="currentcolor"
              fontSize={fontSize}
              value={
                getNumberFormatter(payload.value, 'en-GB', {
                  maximumFractionDigits: 0,
                  minimumFractionDigits: 0,
                }) ?? payload.value
              }
            />
          )}
        />
        <Tooltip
          content={({ active, payload }) => (
            <TooltipContent active={active} payload={payload}>
              {(item) => (
                <div className="mt-1 text-[16px]">
                  <p>
                    {getNumberFormatter(item.value, 'en-GB', {
                      maximumFractionDigits: 0,
                      minimumFractionDigits: 0,
                    })}
                  </p>
                  {'changePercent' in item && item.changePercent ? (
                    <div className="flex items-center gap-2 rounded-lg bg-[#29C9AC10] px-4 py-2">
                      <TrendArrow changeValue={item.changePercent} />
                      <p>{item.changePercent.toFixed(2)}%</p>
                    </div>
                  ) : null}
                </div>
              )}
            </TooltipContent>
          )}
        />
        <Area
          type="linear"
          dataKey="value"
          stroke="#5007BE"
          fillOpacity={1}
          fill="url(#colorValue3)"
          strokeWidth={4}
          connectNulls={false}
          activeDot={{ r: 10 }}
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};
